import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { colors } from "../config";
import { parseToLocal } from "../utilis/methods/localDateTime";
import { useMessenger } from "./MessengerContext";

function formatPreviewTime(value: string | null): string {
  if (!value) return "";
  const d = parseToLocal(value);
  if (!d) return "";
  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs(), "year")) return d.format("DD MMM");
  return d.format("DD/MM/YY");
}

export default function ConversationList() {
  const {
    conversations,
    conversationsLoading,
    activeConversationId,
    openConversation,
  } = useMessenger();

  if (conversationsLoading && conversations.length === 0) {
    return (
      <Box className="flex justify-center items-center h-full p-4">
        <CircularProgress size={28} color="primary" />
      </Box>
    );
  }

  if (conversations.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center h-full p-4 text-center gap-1">
        <Typography variant="body2" color="text.secondary">
          No conversations yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Start a new message to chat with a colleague
        </Typography>
      </Box>
    );
  }

  return (
    <List
      disablePadding
      sx={{ overflowY: "auto", height: "100%", bgcolor: "background.paper" }}
    >
      {conversations.map((conv) => {
        const selected = conv.id === activeConversationId;
        return (
          <ListItemButton
            key={conv.id}
            selected={selected}
            onClick={() => void openConversation(conv.id)}
            alignItems="flex-start"
            sx={{
              py: 1.25,
              px: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&.Mui-selected": { bgcolor: colors.primaryLight },
              "&.Mui-selected:hover": { bgcolor: colors.primaryLight },
            }}
          >
            <ListItemAvatar>
              <Badge
                color="error"
                badgeContent={conv.unreadCount || 0}
                invisible={!conv.unreadCount}
                overlap="circular"
              >
                <Avatar
                  src={conv.peerProfileImage || undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: colors.primaryMain,
                  }}
                >
                  {(conv.peerDisplayName || "?").charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box className="flex justify-between items-center gap-2">
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      fontWeight: conv.unreadCount ? 700 : 500,
                      color: "text.primary",
                    }}
                  >
                    {conv.peerDisplayName || `User ${conv.peerUserId}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: conv.unreadCount
                        ? "primary.main"
                        : "text.secondary",
                    }}
                  >
                    {formatPreviewTime(conv.lastMessageAt || conv.updatedAt)}
                  </Typography>
                </Box>
              }
              secondary={
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: "text.secondary",
                    fontWeight: conv.unreadCount ? 600 : 400,
                  }}
                >
                  {conv.lastMessage || "No messages yet"}
                </Typography>
              }
            />
          </ListItemButton>
        );
      })}
    </List>
  );
}
